import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  AccountService,
  FetchAllUsersIfNeeded,
  UsersState,
} from '@wilson/account';
import {
  defaultColDef,
  defaultGridOptionsFactory,
} from '@wilson/ag-grid/configuration';
import {
  RoleAction,
  RolePermissionSubject,
  User,
  UserQualificationV2,
  WhereOption,
} from '@wilson/interfaces';
import { CustomInfiniteRowModelSelectAllHeaderComponent } from '@wilson/non-domain-specific/ag-grid-custom/ag-grid-select-all';
import { UserQualificationsGateway } from '@wilson/qualification-v2/common';
import { EmptyStateComponent } from '@wilson/shared-components';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridOptions,
  IGetRowsParams,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { isEqual } from 'lodash';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  map,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { UserQualificationsV2ListService } from '../../service/user-qualifications-v2-list.service';
import {
  GetUserQualificationsV2Overview,
  GetUserQualificationsV2OverviewDetails,
  OpenUserQualificationV2OverviewDetailsDrawer,
  ResetUserQualificationsV2OverviewDetails,
} from '../../states/user-qualifications-v2-overview/user-qualifications-v2-overview.action';
import { UserQualificationsV2OverviewState } from '../../states/user-qualifications-v2-overview/user-qualifications-v2-overview.state';
import { BulkCreateQualificationsService } from '../qualification-v2-overview/bulk-create-qualifications/bulk-create-qualifications.service';
import { BulkEditQualificationsService } from '../qualification-v2-overview/bulk-edit-qualifications/bulk-edit-qualifications.service';
import { QualificationDetailsDrawerComponent } from './detail-drawer/qualification-details-drawer.component';
import { ConfirmedByUserRendererComponent } from './renderers/confirmed-by-user-renderer';
import { DateRendererComponent } from './renderers/date-renderer.component';
import { QualificationCategoryRendererComponent } from './renderers/qualification-category.renderer';
import { QualificationLinkRendererComponent } from './renderers/qualification-link-renderer.component';
import { QualificationUserRendererComponent } from './renderers/qualification-user-renderer';
import { StatusQualificationV2IconRendererComponent } from './renderers/status-icon.renderer';
import { ValidTillRendererComponent } from './renderers/valid-till-renderer.component';
import { ShowTextTooltipRendererComponent } from '@wilson/qualifications/core';

@Component({
  selector: 'wilson-qualifications-v2-list',
  templateUrl: './qualifications-v2-list.component.html',
  styleUrls: ['./qualifications-v2-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserQualificationsGateway],
  imports: [
    AgGridAngular,
    AgGridModule,
    CommonModule,
    QualificationDetailsDrawerComponent,
  ],
})
export class QualificationsV2ListComponent implements OnInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private store = inject(Store);
  private readonly bulkCreateQualificationsService = inject(
    BulkCreateQualificationsService,
  );
  private readonly userQualificationsV2ListService = inject(
    UserQualificationsV2ListService,
  );
  private readonly bulkEditQualificationsService = inject(
    BulkEditQualificationsService,
  );
  private readonly router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private readonly accountService = inject(AccountService);

  @Input() statusFilter!: BehaviorSubject<string[]>;
  @Input() categoryFilter!: BehaviorSubject<string[]>;
  @Input() orgUnitFilter!: BehaviorSubject<string[]>;
  @Input() userIdsFilter!: BehaviorSubject<string[]>;
  @Input() userLabelsFilter!: BehaviorSubject<string[]>;
  @Input() searchFilter!: BehaviorSubject<string>;

  @Output() selectedRowsChange = new EventEmitter<UserQualificationV2[]>();

  public can = inject(AbilityServiceSignal).can;
  public readonly RoleAction = RoleAction;
  public readonly RolePermissionSubject = RolePermissionSubject;

  private destroy$ = new Subject<void>();
  private usersWithLabels: User[] = [];
  private filters = {
    statuses: [] as string[],
    category: [] as string[],
    orgUnit: [] as string[],
    userIds: [] as string[],
    userLabels: [] as string[],
    search: '',
    lang: '',
  };
  private readonly defaultSortingFields: Record<string, 'asc' | 'desc'> | null =
    { userName: 'asc' };

  pageSize = this.store.selectSnapshot(
    UserQualificationsV2OverviewState.getPageSize,
  );

  public readonly defaultColDef = defaultColDef;
  readonly gridOptions: GridOptions = {
    ...defaultGridOptionsFactory(),
    rowSelection: 'multiple',
    suppressRowClickSelection: true,
    rowModelType: 'infinite',
    cacheBlockSize: this.pageSize,
    paginationPageSize: this.pageSize,
    pagination: true,
    paginationPageSizeSelector: [50, 100, 150],
    suppressPaginationPanel: false,
    paginationAutoPageSize: false,
    onSortChanged: (params) => this.handleSortChanged(params),
    multiSortKey: 'ctrl',
    loading: true,
  };

  isDrawerVisible = false;
  selectedQualification!: UserQualificationV2;

  public readonly components = {
    selectAllHeaderComponent: CustomInfiniteRowModelSelectAllHeaderComponent,
    noRowsOverlay: EmptyStateComponent,
    qualificationLinkRendererComponent: QualificationLinkRendererComponent,
    statusIconRenderer: StatusQualificationV2IconRendererComponent,
    confirmedByUserIconRendererComponent: ConfirmedByUserRendererComponent,
    qualificationCategoryRendererComponent:
      QualificationCategoryRendererComponent,
    qualificationUserRendererComponent: QualificationUserRendererComponent,
    dateRendererComponent: DateRendererComponent,
    validTillRenderer: ValidTillRendererComponent,
    showTextTooltipRendererComponent: ShowTextTooltipRendererComponent,
  };

  private gridApi!: GridApi;
  protected columnDefs: ColDef[];
  protected showGrid = false;
  public paginationChangedListener = (): void => this.handlePaginationChanged();

  qualifications$ = this.store.select(
    UserQualificationsV2OverviewState.getQualifications,
  );
  isLoading$ = this.store.select(UserQualificationsV2OverviewState.isLoading);
  isDrawerVisible$ = this.store.select(
    UserQualificationsV2OverviewState.isDrawerVisible,
  );
  qualificationDetails$ = this.store.select(
    UserQualificationsV2OverviewState.getQualificationDetails,
  );
  employee$!: Observable<User | undefined>;

  constructor() {
    this.columnDefs = [
      {
        resizable: false,
        sortable: false,
        width: 50,
        checkboxSelection: true,
        headerComponent: 'selectAllHeaderComponent',
        pinned: 'left',
        lockPinned: true,
        lockPosition: true,
      },
      {
        flex: 1,
        resizable: true,
        field: 'qualification.name',
        cellRenderer: 'qualificationLinkRendererComponent',
        headerName: this.translateService.instant(
          'page.qualifications.qualification',
        ),
        sortable: true,
        colId: 'qualificationName',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        resizable: true,
        field: 'user.firstName',
        cellRenderer: 'qualificationUserRendererComponent',
        headerName: this.translateService.instant('page.qualifications.user'),
        sortable: true,
        sort: 'asc',
        colId: 'userName',
      },
      {
        flex: 1,
        field: 'acquiredAt',
        headerName: this.translateService.instant('general.acquired_on'),
        cellRenderer: 'dateRendererComponent',
        sortable: true,
        colId: 'acquiredAt',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        field: 'validFrom',
        headerName: this.translateService.instant('general.valid_from'),
        cellRenderer: 'dateRendererComponent',
        sortable: true,
        colId: 'validFrom',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        field: 'validTill',
        headerName: this.translateService.instant('general.valid_until'),
        cellRenderer: 'validTillRenderer',
        sortable: true,
        colId: 'validTill',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        field: 'status',
        cellRenderer: 'statusIconRenderer',
        headerName: this.translateService.instant('general.status'),
        sortable: true,
        colId: 'status',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        field: 'confirmedByUser',
        cellRenderer: 'confirmedByUserIconRendererComponent',
        headerName: this.translateService.instant(
          'page.qualifications.confirmed',
        ),
        sortable: true,
        colId: 'confirmedByUser',
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
      },
      {
        flex: 1,
        field: 'qualification',
        cellRenderer: 'qualificationCategoryRendererComponent',
        headerName: this.translateService.instant(
          'page.qualifications.category',
        ),
        cellRendererParams: {
          onClick: (event: MouseEvent, data: UserQualificationV2): void => {
            this.handleQualificationClick(data);
          },
        },
        sortable: true,
        colId: 'qualificationCategoryName',
      },
      {
        flex: 1,
        field: 'notes',
        cellRenderer: 'showTextTooltipRendererComponent',
        headerName: this.translateService.instant('page.qualifications.notes'),
        sortable: false,
        colId: 'notes',
      },
    ];
  }

  ngOnInit(): void {
    this.store.dispatch(new FetchAllUsersIfNeeded());
    this.accountService
      .getUsers(undefined, undefined, false, 'labels')
      .pipe(takeUntil(this.destroy$))
      .subscribe((users) => {
        this.usersWithLabels = users;
        // If a label filter was pre-loaded from the URL, the initial grid fetch
        // ran before users were available. Re-fetch now that we have them.
        if (this.filters.userLabels?.length && this.gridApi) {
          this.refreshGrid();
        }
      });
    this.setupBulkEditListeners();

    combineLatest([
      combineLatest([
        this.statusFilter,
        this.categoryFilter,
        this.orgUnitFilter,
        this.userIdsFilter,
        this.userLabelsFilter,
        this.searchFilter,
        this.translateService.onLangChange.pipe(
          startWith({ lang: this.translateService.getCurrentLang() ?? '' }),
          map((event) => ({ lang: event.lang.split('-')[0] })),
        ),
      ]).pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        tap(
          ([
            statuses,
            category,
            orgUnit,
            userIds,
            userLabels,
            search,
            langEvent,
          ]) => {
            this.filters = {
              statuses,
              category,
              orgUnit,
              userIds,
              userLabels,
              search,
              lang: langEvent.lang,
            };
          },
        ),
      ),
      this.bulkCreateQualificationsService.bulkCreatedQualifications$.pipe(
        startWith(undefined),
      ),
      this.userQualificationsV2ListService.bulkDeleteQualifications$.pipe(
        startWith(undefined),
      ),
      this.bulkEditQualificationsService.bulkEditedQualifications$.pipe(
        startWith(undefined),
      ),
      this.userQualificationsV2ListService.bulkImportedQualifications$.pipe(
        startWith(undefined),
      ),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.gridApi) {
          this.refreshGrid();
          this.store.dispatch(new ResetUserQualificationsV2OverviewDetails());
          this.userQualificationsV2ListService.clearSelection();
        }
      });
  }

  ngOnDestroy(): void {
    this.gridApi.removeEventListener(
      'paginationChanged',
      this.paginationChangedListener,
    );
    this.store.dispatch(new ResetUserQualificationsV2OverviewDetails());
    this.userQualificationsV2ListService.clearSelection();
    this.destroy$.next();
    this.destroy$.complete();
  }

  onGridReady(params: { api: GridApi }): void {
    this.gridApi = params.api;
    this.gridApi.removeEventListener(
      'paginationChanged',
      this.paginationChangedListener,
    );
    this.gridApi.setGridOption(
      'datasource',
      this.createDataSource(this.defaultSortingFields),
    );
    this.gridApi.addEventListener('selectionChanged', () => {
      const selectedRows = this.gridApi.getSelectedRows();
      this.selectedRowsChange.emit(selectedRows);
    });
  }

  public handleOnCloseDetailsDrawer(): void {
    this.store.dispatch(
      new OpenUserQualificationV2OverviewDetailsDrawer(false),
    );
  }

  public onSelectionChanged(event: SelectionChangedEvent): void {
    const selectedRows = event.api.getSelectedRows();
    this.userQualificationsV2ListService.setSelectedQualifications(
      selectedRows,
    );
  }

  private refreshGrid(): void {
    if (this.gridApi) {
      this.gridApi.removeEventListener(
        'paginationChanged',
        this.paginationChangedListener,
      );
      this.gridApi.setGridOption(
        'datasource',
        this.createDataSource(this.defaultSortingFields),
      );
      this.gridApi.resetColumnState();
    }
  }

  private createDataSource(
    sortFields?: Record<string, 'asc' | 'desc'> | null,
  ): {
    getRows: (params: IGetRowsParams) => void;
  } {
    return {
      getRows: (params: IGetRowsParams): void => {
        this.gridApi.removeEventListener(
          'paginationChanged',
          this.paginationChangedListener,
        );

        let pageSize = this.gridApi.paginationGetPageSize();
        const pageNumber = Math.floor(params.startRow / pageSize) + 1;

        const queryParams = this.activatedRoute.snapshot.queryParams;
        if (queryParams['pageSize']) {
          pageSize = queryParams['pageSize'] as number;
        }

        this.gridApi.updateGridOptions({
          cacheBlockSize: pageSize,
          paginationPageSize: pageSize,
        });

        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: {
            pageSize,
          },
          queryParamsHandling: 'merge',
        });

        if (pageNumber < 1) {
          params.failCallback();
          return;
        }

        const hasActiveUserFilter =
          (this.filters.userIds?.length ?? 0) > 0 ||
          (this.filters.userLabels?.length ?? 0) > 0;
        const effectiveUserIds = this.resolveEffectiveUserIds();
        if (hasActiveUserFilter && effectiveUserIds.length === 0) {
          params.successCallback([], 0);
          this.gridApi.setGridOption('loading', false);
          this.gridApi.showNoRowsOverlay();
          return;
        }

        this.gridApi.setGridOption('loading', true);
        this.showGrid = false;

        const whereParams = this.setWhereOptions(sortFields);

        this.store
          .dispatch(
            new GetUserQualificationsV2Overview({
              pageNumber,
              pageSize,
              relations: ['qualification'],
              lang: this.filters.lang,
              ...whereParams,
            }),
          )
          .pipe(
            catchError(() => {
              params.failCallback();
              return of({ data: [], pagination: { totalRecords: 0 } });
            }),
            finalize(() => {
              this.gridApi.setGridOption('loading', false);
              this.showGrid = true;
              this.gridApi.addEventListener(
                'paginationChanged',
                this.paginationChangedListener,
              );
            }),
            switchMap(() =>
              combineLatest([
                this.store.select(
                  UserQualificationsV2OverviewState.getQualifications,
                ),
                this.store.select(
                  UserQualificationsV2OverviewState.getTotalRecords,
                ),
              ]),
            ),
            take(1),
          )
          .subscribe(([qualifications, totalRecords]) => {
            if (qualifications?.length) {
              params.successCallback(qualifications, totalRecords);
            } else {
              params.successCallback([], 0);
              this.gridApi.setGridOption('loading', false);
              this.gridApi.showNoRowsOverlay();
            }
          });
      },
    };
  }

  private resolveEffectiveUserIds(): string[] {
    const { userIds, userLabels } = this.filters;
    if (!userLabels?.length) {
      return userIds ?? [];
    }
    const labelMatchedIds = this.usersWithLabels
      .filter((user) =>
        user.labels?.some((label) => userLabels.includes(label.id as string)),
      )
      .map((user) => user.id as string);

    if (userIds?.length) {
      return labelMatchedIds.filter((id) => userIds.includes(id));
    }
    return labelMatchedIds;
  }

  private setWhereOptions(
    sortFields?: Record<string, 'asc' | 'desc'> | null,
  ): WhereOption {
    let whereOptions = {};

    const effectiveUserIds = this.resolveEffectiveUserIds();
    if (effectiveUserIds.length) {
      whereOptions = {
        ...whereOptions,
        userIds: effectiveUserIds,
      };
    }

    if (this.filters.category?.length) {
      whereOptions = {
        ...whereOptions,
        qualificationCategoryIds: this.filters.category,
      };
    }

    if (this.filters.orgUnit?.length) {
      whereOptions = {
        ...whereOptions,
        userOrganizationalUnitIds: this.filters.orgUnit,
      };
    }

    if (this.filters.search) {
      whereOptions = {
        ...whereOptions,
        searchText: this.filters.search,
      };
    }

    if (this.filters.statuses?.length) {
      whereOptions = {
        ...whereOptions,
        statuses: this.filters.statuses,
      };
    }

    if (sortFields && Object.keys(sortFields).length > 0) {
      whereOptions = {
        ...whereOptions,
        order: sortFields,
      };
    }

    whereOptions = {
      ...whereOptions,
      lang: this.filters.lang,
    };

    return whereOptions;
  }

  private handleQualificationClick(data: UserQualificationV2): void {
    this.selectedQualification = data;
    this.employee$ = this.store.select(
      UsersState.userById(this.selectedQualification.userId),
    );

    this.store
      .select(UserQualificationsV2OverviewState.getQualificationDetails)
      .pipe(
        take(1),
        tap((currentDetails) => {
          if (this.isSameQualification(currentDetails)) {
            this.openDrawerWithExistingDetails();
          } else {
            this.fetchAndShowNewDetails(
              this.selectedQualification.id,
              this.selectedQualification.userId,
            );
          }
        }),
      )
      .subscribe();
  }

  private isSameQualification(
    currentDetails: UserQualificationV2 | null,
  ): boolean {
    return currentDetails?.id === this.selectedQualification.id;
  }

  private openDrawerWithExistingDetails(): void {
    this.store.dispatch(new OpenUserQualificationV2OverviewDetailsDrawer(true));
  }

  private fetchAndShowNewDetails(
    userQualificationId: string,
    userId: string,
  ): void {
    this.store.dispatch(
      new GetUserQualificationsV2OverviewDetails(userQualificationId, userId),
    );
  }

  private handleSortChanged(params: { api: GridApi }): void {
    const sortModel = params.api.getColumnState();
    const sortFields = sortModel
      .filter((sort) => sort.sort)
      .reduce(
        (acc, sort) => ({
          ...acc,
          [sort.colId]: sort.sort?.toLowerCase(),
        }),
        {},
      );

    this.refreshGridWithSort(sortFields);
  }

  private refreshGridWithSort(
    sortFields: Record<string, 'asc' | 'desc'> | null,
  ): void {
    if (this.gridApi) {
      this.gridApi.removeEventListener(
        'paginationChanged',
        this.paginationChangedListener,
      );
      const dataSource = this.createDataSource(sortFields);
      this.gridApi.setGridOption('datasource', dataSource);
    }
  }

  private setupBulkEditListeners(): void {
    merge(
      this.bulkEditQualificationsService.bulkEditCanceled$,
      this.bulkEditQualificationsService.bulkEditedQualifications$,
    )
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          if (this.gridApi) {
            this.gridApi.deselectAll();
            this.userQualificationsV2ListService.clearSelection();
          }
        }),
      )
      .subscribe();
  }

  private handlePaginationChanged(): void {
    const pageSize = this.gridApi.paginationGetPageSize();

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        pageSize,
      },
      queryParamsHandling: 'merge',
    });

    this.gridApi.setGridOption('cacheBlockSize', pageSize);
  }
}
